import React, { useState } from 'react';
import { X, Upload, FileText, CheckCircle, XCircle, Download } from 'lucide-react';
import api from '../../services/api';

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultRole?: string;
}

interface BulkUserResult {
  email: string;
  success: boolean;
  message: string;
  user_id?: number;
  generated_password?: string;
}

type UploadMode = 'new_users' | 'legacy_migration';

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ isOpen, onClose, onSuccess, defaultRole }) => {
  const [mode, setMode] = useState<UploadMode>('new_users');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<BulkUserResult[]>([]);
  const [stats, setStats] = useState({ total: 0, successful: 0, failed: 0 });
  const [sendEmails, setSendEmails] = useState(true);

  const downloadTemplate = () => {
    let csvContent: string;
    let filename: string;
    const roleToUse = defaultRole || 'customer';
    const roleName = roleToUse === 'rider' ? 'dispatcher' : roleToUse;
    
    if (mode === 'new_users') {
      if (defaultRole === 'rider') {
        csvContent = 'email,phone,first_name,last_name,role_name,password\n' +
          'john.doe@example.com,+234 800 123 4567,John,Doe,rider,\n' +
          'jane.smith@example.com,+234 800 234 5678,Jane,Smith,rider,\n' +
          'mike.johnson@example.com,+234 800 345 6789,Mike,Johnson,rider,CustomPass123!';
        filename = 'volexx_dispatchers_template.csv';
      } else {
        csvContent = 'email,phone,first_name,last_name,role_name,password\n' +
          'john.doe@example.com,+234 800 123 4567,John,Doe,customer,\n' +
          'jane.smith@example.com,+234 800 234 5678,Jane,Smith,rider,\n' +
          'merchant@example.com,+234 800 345 6789,Mike,Johnson,merchant,CustomPass123!';
        filename = 'volexx_new_users_template.csv';
      }
    } else {
      if (defaultRole === 'rider') {
        csvContent = 'name,phone,email,role_name,wallet_balance\n' +
          'John Doe,+234 800 123 4567,john.doe@example.com,rider,0\n' +
          'Jane Smith,+234 800 234 5678,jane.smith@example.com,rider,1500.50\n' +
          'Mike Johnson,+234 800 345 6789,mike.johnson@example.com,rider,750.25';
        filename = 'volexx_legacy_dispatchers_migration_template.csv';
      } else {
        csvContent = 'name,phone,email,role_name,wallet_balance\n' +
          'John Doe,+234 800 123 4567,john.doe@example.com,customer,0\n' +
          'Jane Smith,+234 800 234 5678,jane.smith@example.com,rider,1500.50\n' +
          'Mike Johnson,+234 800 345 6789,merchant@example.com,merchant,750.25';
        filename = 'volexx_legacy_migration_template.csv';
      }
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/^"/, '').replace(/"$/, ''));
    const users = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"/, '').replace(/"$/, ''));
      const user: any = {};

      headers.forEach((header, index) => {
        if (values[index]) {
          user[header] = values[index];
        }
      });

      // Validate based on mode
      if (mode === 'new_users') {
        if (user.email && user.phone && user.first_name && user.last_name) {
          if (!user.role_name && defaultRole) {
            user.role_name = defaultRole;
          }
          if (user.role_name) {
            users.push(user);
          }
        }
      } else {
        // Legacy migration mode - handle multiple CSV formats flexibly
        
        // Handle name: support "name" OR "First Name" + "Last Name"
        let fullName = user.name || '';
        if (!fullName && (user['First Name'] || user['Last Name'])) {
          const firstName = user['First Name'] || '';
          const lastName = user['Last Name'] || '';
          fullName = `${firstName} ${lastName}`.trim();
        }
        
        // Handle role_name: support "role_name" OR "Agent Type"
        let roleName = user.role_name || '';
        if (!roleName && user['Agent Type']) {
          // Map CAPTIVE/FREELANCER to 'rider'
          const agentType = user['Agent Type'].toUpperCase();
          if (agentType === 'CAPTIVE' || agentType === 'FREELANCER') {
            roleName = 'rider';
          }
        }
        
        // Handle phone: support "phone" OR "Phone"
        const phone = user.phone || user.Phone || '';
        
        // Handle email: support "email" OR "Email"
        const email = user.email || user.Email || '';
        
        // Handle wallet_balance: support "wallet_balance" OR "Wallet Balance"
        let walletBalance = user.wallet_balance || user['Wallet Balance'] || '0';
        walletBalance = parseFloat(walletBalance) || 0;
        
        // Skip if essential fields are missing
        if (!email || !phone || !fullName || !roleName) {
          continue;
        }
        
        // Create normalized user object for backend
        users.push({
          name: fullName,
          phone: phone,
          email: email,
          role_name: roleName,
          wallet_balance: walletBalance
        });
      }
    }

    return users;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please upload a CSV file');
        return;
      }
      setFile(selectedFile);
      setError('');
      setResults([]);
      setStats({ total: 0, successful: 0, failed: 0 });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    try {
      const text = await file.text();
      const users = parseCSV(text);

      if (users.length === 0) {
        setError('No valid users found in CSV file');
        setLoading(false);
        return;
      }

      const maxUsers = mode === 'new_users' ? 100 : 200;
      if (users.length > maxUsers) {
        setError(`Maximum ${maxUsers} users can be uploaded at once`);
        setLoading(false);
        return;
      }

      const endpoint = mode === 'new_users' ? '/admin/users/bulk-create' : '/admin/users/bulk-migrate';
      const emailKey = mode === 'new_users' ? 'send_welcome_emails' : 'send_migration_emails';
      
      const response = await api.post(endpoint, {
        users,
        [emailKey]: sendEmails
      });

      setResults(response.data.results);
      setStats({
        total: response.data.total,
        successful: response.data.successful,
        failed: response.data.failed
      });

      if (response.data.successful > 0) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload users');
    } finally {
      setLoading(false);
    }
  };

  const downloadResults = () => {
    const csvContent = 'email,status,message,generated_password\n' +
      results.map(r => 
        `${r.email},${r.success ? 'Success' : 'Failed'},${r.message},${r.generated_password || ''}`
      ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_upload_results_${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Bulk Upload Users</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Mode Toggle */}
          <div className="flex gap-3 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => {
                setMode('new_users');
                setFile(null);
                setResults([]);
                setError('');
              }}
              className={`flex-1 px-4 py-3 rounded-md font-medium transition-all ${
                mode === 'new_users'
                  ? 'bg-white text-sky-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ➕ Create New Users
            </button>
            <button
              onClick={() => {
                setMode('legacy_migration');
                setFile(null);
                setResults([]);
                setError('');
              }}
              className={`flex-1 px-4 py-3 rounded-md font-medium transition-all ${
                mode === 'legacy_migration'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🔄 Migrate Legacy Users
            </button>
          </div>

          {/* Instructions */}
          <div className={`border rounded-lg p-4 ${mode === 'new_users' ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
            <h3 className={`font-semibold mb-2 ${mode === 'new_users' ? 'text-blue-900' : 'text-orange-900'}`}>
              📋 {mode === 'new_users' ? 'Create New Users' : 'Migrate Legacy Users'}
            </h3>
            {mode === 'new_users' ? (
              <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                <li>Download the CSV template</li>
                <li>Fill in user details (email, phone, first_name, last_name, role_name)</li>
                <li>Leave password blank to auto-generate secure passwords</li>
                <li>Upload the completed CSV file</li>
                <li>Valid roles: customer, rider, merchant, admin</li>
                <li>Maximum 100 users per upload</li>
              </ol>
            ) : (
              <ol className="text-sm text-orange-800 space-y-1 ml-4 list-decimal">
                <li>Download the legacy migration CSV template</li>
                <li>Fill in: name (full name), phone, email, role_name, wallet_balance</li>
                <li>Users will be auto-approved (migrated from verified platform)</li>
                <li>Wallet balances will be preserved from legacy platform</li>
                <li>Passwords will be auto-generated and sent via email</li>
                <li>Valid roles: customer, rider, merchant</li>
                <li>Maximum 200 users per upload</li>
              </ol>
            )}
            <button
              onClick={downloadTemplate}
              className={`mt-3 flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors text-sm ${
                mode === 'new_users' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-orange-600 hover:bg-orange-700'
              }`}
            >
              <Download className="w-4 h-4" />
              Download CSV Template
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Upload Form */}
          {results.length === 0 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload CSV File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="cursor-pointer inline-block px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
                  >
                    Choose CSV File
                  </label>
                  {file && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-gray-700">
                      <FileText className="w-5 h-5" />
                      <span className="font-medium">{file.name}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="send_welcome_emails"
                  checked={sendEmails}
                  onChange={(e) => setSendEmails(e.target.checked)}
                  className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                />
                <label htmlFor="send_welcome_emails" className="ml-2 text-sm text-gray-700">
                  Send welcome emails to all users
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !file}
                  className="flex-1 px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Uploading...' : 'Upload Users'}
                </button>
              </div>
            </form>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
                  <p className="text-sm text-gray-600">Total</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{stats.successful}</p>
                  <p className="text-sm text-green-600">Successful</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Password</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {results.map((result, index) => (
                      <tr key={index} className={result.success ? 'bg-white' : 'bg-red-50'}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {result.success ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{result.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{result.message}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">
                          {result.generated_password || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={downloadResults}
                  className="flex-1 px-6 py-3 border border-sky-600 text-sky-600 rounded-lg hover:bg-sky-50 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download Results
                </button>
                <button
                  onClick={() => {
                    setResults([]);
                    setFile(null);
                    setStats({ total: 0, successful: 0, failed: 0 });
                  }}
                  className="flex-1 px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors font-medium"
                >
                  Upload More Users
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
